import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineTask {
  id: string;
  title: string;
  description: string;
  month: string;
  week: string;
  category: 'Setup' | 'Planting' | 'Maintenance' | 'Harvest';
  priority: 'High' | 'Medium' | 'Low';
  estimatedTime: string;
  materials?: string[];
}

interface ImplementationTimelineProps {
  tasks: TimelineTask[];
}

const ImplementationTimeline = ({ tasks }: ImplementationTimelineProps) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Setup': return 'bg-primary text-primary-foreground';
      case 'Planting': return 'bg-success text-success-foreground';
      case 'Maintenance': return 'bg-warning text-warning-foreground';
      case 'Harvest': return 'bg-accent text-accent-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'High': return 'ExclamationCircleIcon';
      case 'Medium': return 'ExclamationTriangleIcon';
      case 'Low': return 'InformationCircleIcon';
      default: return 'QuestionMarkCircleIcon';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-error';
      case 'Medium': return 'text-warning';
      case 'Low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const groupedTasks = tasks.reduce((acc, task) => {
    if (!acc[task.month]) {
      acc[task.month] = [];
    }
    acc[task.month].push(task);
    return acc;
  }, {} as Record<string, TimelineTask[]>);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Implementation Timeline</h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="CalendarDaysIcon" size={16} />
          <span>12-Month Plan</span>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedTasks).map(([month, monthTasks]) => (
          <div key={month} className="relative">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <h4 className="text-md font-semibold text-foreground">{month}</h4>
              <div className="flex-1 h-px bg-border"></div>
            </div>

            <div className="ml-6 space-y-3">
              {monthTasks.map((task) => (
                <div key={task.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h5 className="font-medium text-foreground">{task.title}</h5>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(task.category)}`}>
                          {task.category}
                        </span>
                        <div className={`flex items-center space-x-1 ${getPriorityColor(task.priority)}`}>
                          <Icon name={getPriorityIcon(task.priority) as any} size={14} />
                          <span className="text-xs font-medium">{task.priority}</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                      
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="CalendarIcon" size={12} />
                          <span>{task.week}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="ClockIcon" size={12} />
                          <span>{task.estimatedTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {task.materials && task.materials.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="WrenchScrewdriverIcon" size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">Required Materials:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {task.materials.map((material, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-surface text-xs text-foreground rounded border border-border"
                          >
                            {material}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImplementationTimeline;